import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import {
  toolSettingOf,
  type PlaygroundAgentInfo,
  type PlaygroundSpec,
  type PlaygroundToolDefinition,
  type PlaygroundToolGroup,
} from '@repo/shared-types';
import { Input, Select, Textarea } from '../ui/input';
import { Toggle } from '../ui/toggle';
import { cn } from '../../lib/utils';

const GROUP_LABEL: Record<PlaygroundToolGroup, string> = {
  gather: 'Gather context',
  decide: 'Decide',
  check: 'Check its own work',
  settle: 'Settle',
};

const GROUP_ORDER: PlaygroundToolGroup[] = ['gather', 'decide', 'check', 'settle'];

/**
 * The configuration surface: which tools the agent gets, what each one's description tells it, and
 * the prompt and parameters it runs under.
 *
 * Every field is an *override* and empty means "whatever the deployment uses", which is why the
 * placeholders carry the live defaults rather than the boxes carrying them as values. A form that
 * pre-filled today's default would silently stop tracking production the moment the default changed
 * — and the one config everyone needs is the one that is exactly what is shipped.
 */
export function SpecEditor({
  agent,
  spec,
  onChange,
  disabled,
}: {
  agent: PlaygroundAgentInfo;
  spec: PlaygroundSpec;
  onChange: (next: PlaygroundSpec) => void;
  disabled?: boolean;
}) {
  const [openPrompt, setOpenPrompt] = useState<'system' | 'user' | null>(null);

  const setTool = (name: string, patch: { enabled?: boolean; guidance?: string }) => {
    const current = toolSettingOf(spec, name);
    const next = {
      name,
      enabled: patch.enabled ?? current.enabled,
      ...(patch.guidance !== undefined
        ? patch.guidance.trim()
          ? { guidance: patch.guidance }
          : {}
        : current.guidance
          ? { guidance: current.guidance }
          : {}),
    };

    const rest = spec.tools.filter((tool) => tool.name !== name);
    // A tool that is on with no guidance says nothing the default does not, so it is dropped rather
    // than stored — which is what lets "is this the shipped agent" stay a question about an empty
    // array instead of a walk through nine entries checking they are all neutral.
    const keep = next.enabled && !next.guidance?.trim() ? rest : [...rest, next];
    onChange({ ...spec, tools: keep });
  };

  const disabledCount = agent.tools.filter((tool) => !toolSettingOf(spec, tool.name).enabled).length;
  const guidedCount = agent.tools.filter((tool) => toolSettingOf(spec, tool.name).guidance?.trim()).length;

  return (
    <div className="space-y-5">
      {/* ── The roster ───────────────────────────────────────────────────── */}
      <section>
        <div className="flex items-baseline justify-between mb-2">
          <h3 className="text-xs font-semibold text-ink">Tools</h3>
          <p className="text-[10px] text-ink-faint">
            {disabledCount === 0 && guidedCount === 0
              ? 'the full roster, unchanged'
              : [
                  disabledCount > 0 ? `${disabledCount} off` : '',
                  guidedCount > 0 ? `${guidedCount} with guidance` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
          </p>
        </div>

        <div className="space-y-3">
          {GROUP_ORDER.filter((group) => agent.tools.some((tool) => tool.group === group)).map((group) => (
            <div key={group}>
              <p className="px-0.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
                {GROUP_LABEL[group]}
              </p>
              <div className="space-y-1.5">
                {agent.tools
                  .filter((tool) => tool.group === group)
                  .map((tool) => (
                    <ToolRow
                      key={tool.name}
                      tool={tool}
                      enabled={toolSettingOf(spec, tool.name).enabled}
                      guidance={toolSettingOf(spec, tool.name).guidance ?? ''}
                      disabled={disabled}
                      onToggle={(enabled) => setTool(tool.name, { enabled })}
                      onGuidance={(guidance) => setTool(tool.name, { guidance })}
                    />
                  ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Prompts ──────────────────────────────────────────────────────── */}
      <section>
        <h3 className="text-xs font-semibold text-ink mb-2">Prompt</h3>
        {agent.prompt.error && (
          <p className="mb-2 flex items-start gap-1.5 rounded-md border border-warning-ring bg-warning-bg px-2.5 py-1.5 text-[11px] text-warning-text">
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            <span>
              Couldn’t load the live prompt ({agent.prompt.error}). Leaving these empty still works —
              the run fetches them itself.
            </span>
          </p>
        )}
        <div className="space-y-1.5">
          <PromptOverride
            label="System prompt"
            name={agent.prompts.system}
            live={agent.prompt.system}
            value={spec.systemPrompt}
            open={openPrompt === 'system'}
            onOpen={() => setOpenPrompt(openPrompt === 'system' ? null : 'system')}
            onChange={(systemPrompt) => onChange({ ...spec, systemPrompt })}
            disabled={disabled}
          />
          <PromptOverride
            label="User prompt"
            name={agent.prompts.user}
            live={agent.prompt.user}
            value={spec.userPrompt}
            open={openPrompt === 'user'}
            onOpen={() => setOpenPrompt(openPrompt === 'user' ? null : 'user')}
            onChange={(userPrompt) => onChange({ ...spec, userPrompt })}
            disabled={disabled}
            hint={
              agent.input === 'question'
                ? 'Keep {{query}} — without it the agent is never told the question.'
                : 'Keep {{items}} — without it the agent is handed no content at all.'
            }
          />
        </div>
      </section>

      {/* ── Parameters ───────────────────────────────────────────────────── */}
      <section>
        <h3 className="text-xs font-semibold text-ink mb-2">Parameters</h3>
        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Model">
            <Input
              value={spec.model ?? ''}
              onChange={(e) => onChange({ ...spec, model: e.target.value || null })}
              placeholder={agent.defaults.model}
              disabled={disabled}
              className="text-xs"
            />
          </Field>
          <Field label="Reasoning">
            <Select
              value={spec.reasoningEffort ?? ''}
              onChange={(e) =>
                onChange({ ...spec, reasoningEffort: (e.target.value || null) as PlaygroundSpec['reasoningEffort'] })
              }
              disabled={disabled}
              className="text-xs"
            >
              <option value="">Default ({agent.defaults.reasoningEffort})</option>
              <option value="off">Off</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </Select>
          </Field>
          <Field label="Step budget" hint="What the loop may spend before it is stopped.">
            <Input
              type="number"
              min={1}
              max={200}
              value={spec.maxSteps ?? ''}
              onChange={(e) => onChange({ ...spec, maxSteps: e.target.value ? Number(e.target.value) : null })}
              placeholder={String(agent.defaults.maxSteps)}
              disabled={disabled}
              className="text-xs"
            />
          </Field>
          {agent.usesMinConfidence && (
            <Field
              label="Confidence floor"
              hint="Under this, an assignment is recorded but places nothing."
            >
              <Input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={spec.minConfidence ?? ''}
                onChange={(e) => onChange({ ...spec, minConfidence: e.target.value ? Number(e.target.value) : null })}
                placeholder={String(agent.defaults.minConfidence ?? '')}
                disabled={disabled}
                className="text-xs"
              />
            </Field>
          )}
        </div>
        <p className="mt-2 text-[10px] leading-relaxed text-ink-faint">
          Empty means the deployment’s own value, shown as the placeholder — so a config left alone
          keeps tracking production instead of freezing today’s numbers.
        </p>
      </section>
    </div>
  );
}

function ToolRow({
  tool,
  enabled,
  guidance,
  disabled,
  onToggle,
  onGuidance,
}: {
  tool: PlaygroundToolDefinition;
  enabled: boolean;
  guidance: string;
  disabled?: boolean;
  onToggle: (enabled: boolean) => void;
  onGuidance: (guidance: string) => void;
}) {
  const [open, setOpen] = useState(Boolean(guidance));

  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2.5 transition-colors',
        enabled ? 'border-border bg-card' : 'border-border-subtle bg-surface-subtle',
      )}
    >
      <div className="flex items-start gap-2.5">
        <Toggle checked={enabled} onChange={onToggle} disabled={disabled} label={tool.name} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <code className={cn('text-[11px] font-medium', enabled ? 'text-ink' : 'text-ink-faint')}>
              {tool.name}
            </code>
            {tool.structural && (
              <span
                title="Removing this changes how the loop can end, not just what the agent can see."
                className="rounded bg-surface-muted px-1.5 py-px text-[9px] font-medium text-ink-muted"
              >
                structural
              </span>
            )}
            {guidance.trim() && enabled && (
              <span className="rounded bg-info-bg px-1.5 py-px text-[9px] font-medium text-info-text">
                guided
              </span>
            )}
          </div>
          <p className={cn('mt-0.5 text-[11px] leading-relaxed', enabled ? 'text-ink-muted' : 'text-ink-faint')}>
            {enabled ? tool.summary : tool.withoutIt}
          </p>
        </div>

        {enabled && (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="flex-shrink-0 text-[10px] text-ink-faint transition-colors hover:text-ink"
          >
            {open ? 'Hide' : guidance.trim() ? 'Edit' : 'Guide'}
          </button>
        )}
      </div>

      {enabled && open && (
        <div className="mt-2 pl-9">
          <Textarea
            value={guidance}
            onChange={(e) => onGuidance(e.target.value)}
            disabled={disabled}
            rows={3}
            placeholder={`When should the agent reach for ${tool.name}, and what should it do with what comes back?`}
            className="min-h-[64px] text-[11px] leading-relaxed"
          />
          <p className="mt-1 text-[10px] leading-relaxed text-ink-faint">
            Appended to this tool’s own description, so the agent reads it at the moment it is
            choosing whether to call it — not once, forty turns earlier, in the system prompt.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * One prompt override, opening on the live text.
 *
 * "Start from the deployed prompt" is the only useful starting point — an empty box invites writing
 * a prompt from scratch, which is not the experiment anyone means to run — so the live text is one
 * click away and Reset puts the field back to unset rather than back to a copy of it.
 */
function PromptOverride({
  label,
  name,
  live,
  value,
  open,
  onOpen,
  onChange,
  disabled,
  hint,
}: {
  label: string;
  name: string;
  live: string | null;
  value: string | null;
  open: boolean;
  onOpen: () => void;
  onChange: (next: string | null) => void;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <div className={cn('rounded-lg border', value ? 'border-orange-300 dark:border-orange-800' : 'border-border')}>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        {open ? <ChevronDown size={13} className="text-ink-faint" /> : <ChevronRight size={13} className="text-ink-faint" />}
        <span className="text-[11px] font-medium text-ink">{label}</span>
        <code className="truncate text-[10px] text-ink-faint">{name}</code>
        <span className="ml-auto text-[10px] text-ink-faint">
          {value ? `overridden · ${value.length.toLocaleString()} chars` : 'live'}
        </span>
      </button>

      {open && (
        <div className="border-t border-border-subtle px-3 py-2.5">
          <div className="mb-1.5 flex items-center gap-3">
            {live !== null && value === null && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(live)}
                className="text-[10px] text-orange-500 transition-colors hover:text-orange-700 disabled:opacity-40"
              >
                Start from the deployed prompt
              </button>
            )}
            {value !== null && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(null)}
                className="flex items-center gap-1 text-[10px] text-ink-faint transition-colors hover:text-ink disabled:opacity-40"
              >
                <RotateCcw size={10} />
                Back to the live prompt
              </button>
            )}
            {hint && <span className="ml-auto text-[10px] text-ink-faint">{hint}</span>}
          </div>

          <Textarea
            value={value ?? live ?? ''}
            readOnly={value === null}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            rows={14}
            className={cn(
              'font-mono text-[10px] leading-relaxed',
              value === null && 'opacity-60',
            )}
            placeholder={live === null ? 'The live prompt could not be loaded. Paste one here to override it.' : ''}
          />
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-medium text-ink-secondary">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[10px] leading-relaxed text-ink-faint">{hint}</span>}
    </label>
  );
}
